import React, { useEffect, useState } from 'react';
import { Tbody, Tr, Td, Box } from '@chakra-ui/react';
import { Button, Colors, Icon, Text } from '@mercator-gcp/grout';
import { CompanyFilterResponse } from '@mercator-gcp/types/elastic_search_service/CompanyFilter';
import { useNavigate } from 'react-router-dom';
import { EmptyField } from 'components/elements//EmptyField';
import { Spinner } from 'components/elements/Spinner';
import Tooltip from 'components/elements/Tooltip';
import { formatCompanyName, formatInteger } from 'utils/format';
import { companyDetailPageUrl } from 'utils/navigation';
import { getDomainAndExtension, isVisibleCompanyURL } from 'utils/url';
import type { TColumnProps } from 'components/elements/LeadsTable/types';

const rowStyle = {
  backgroundColor: 'transparent',
  '&:hover': {
    cursor: 'pointer',
    td: {
      background: Colors.ArmyGreen100,
    },
  },
  td: {
    backgroundColor: Colors.White,
  },
};

const projectCitiesStyle = {
  display: '-webkit-box',
  overflow: 'hidden',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 3, // Limit text to 3 lines
  textOverflow: 'ellipsis',
  whiteSpace: 'normal',
};

type Props = TColumnProps & {
  items?: CompanyFilterResponse['results'];
  isLoading: boolean;
  onOpenSaveModal?: (companyId: number) => void;
  justSavedCompanyId?: number | null;
  clearJustSaved?: () => void;
};

const SaveButtonCell = ({
  companyId,
  onOpenSaveModal,
  showSuccess,
  clearJustSaved,
}: {
  companyId: number;
  onOpenSaveModal?: (companyId: number) => void;
  showSuccess?: boolean;
  clearJustSaved?: () => void;
}) => {
  const [recentlyChecked, setRecentlyChecked] = useState(false);
  useEffect(() => {
    setRecentlyChecked(showSuccess || false);
  }, [showSuccess]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (clearJustSaved) {
        clearJustSaved();
      }
    }, 2000);

    return () => {
      clearTimeout(handler);
    };
  }, [clearJustSaved]);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (recentlyChecked) {
      return;
    }
    e.stopPropagation();
    if (onOpenSaveModal) {
      onOpenSaveModal(companyId);
    }
  };

  return (
    <Box display="flex" alignItems="center" justifyContent="flex-start">
      <Button
        variant={recentlyChecked ? 'primary' : 'functional'}
        onClick={handleClick}
        aria-label="Save company to group"
        // isDisabled={recentlyChecked}
      >
        {recentlyChecked ? (
          <Icon name={Icon.Name.Check} />
        ) : (
          <Icon name={Icon.Name.Plus} />
        )}
      </Button>
    </Box>
  );
};

const TableBody = ({
  items,
  isLoading,
  onOpenSaveModal,
  justSavedCompanyId,
  clearJustSaved,
}: Props) => {
  const navigate = useNavigate();

  if (!items) {
    return null;
  }

  const onRowClick = (
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    companyUUID: string,
  ) => {
    const targetUrl = companyDetailPageUrl(companyUUID);

    if (
      event.metaKey || // Cmd (Mac)
      event.ctrlKey || // Ctrl (Windows/Linux)
      event.shiftKey || // Shift+Click
      event.altKey // Alt
    ) {
      window.open(targetUrl, '_blank', 'rel=noopener noreferrer');
    } else {
      navigate(targetUrl);
    }
  };

  if (isLoading) {
    return (
      <Tbody>
        <Tr>
          <Td colSpan={100}>
            <Spinner />
          </Td>
        </Tr>
      </Tbody>
    );
  }

  return (
    <Tbody>
      {items?.map((company) => {
        const total = company.num_total_projects ?? 0;
        const active = company.num_active_projects ?? 0;

        const completedProjectsCount = total - active >= 0 ? total - active : 0;

        const projectCities = company.project_cities
          ? company.project_cities.join(', ')
          : '';
        const companyTypes = company.company_types
          ? company.company_types.join(', ')
          : '';

        const isWebsiteVisible = isVisibleCompanyURL(company.website || '');

        return (
          <Tr
            id={`company-search-body-table-row-${company.uuid}`}
            key={`company-search-body-table-row-${company.uuid}`}
            onClick={(e) => onRowClick(e, company.uuid)}
            sx={rowStyle}
          >
            <Td>
              <SaveButtonCell
                companyId={company.id}
                onOpenSaveModal={onOpenSaveModal}
                showSuccess={justSavedCompanyId === company.id}
                clearJustSaved={clearJustSaved}
              />
            </Td>
            <Td>
              <Text.BodyBold>{formatCompanyName(company.name)}</Text.BodyBold>
            </Td>
            <Td>
              <Text.Body>{companyTypes}</Text.Body>
            </Td>
            <Td>
              <Tooltip tooltipText={projectCities} fullWidth>
                <Text.Body sx={projectCitiesStyle}>{projectCities}</Text.Body>
              </Tooltip>
            </Td>
            <Td>
              {company.website && isWebsiteVisible ? (
                <>{getDomainAndExtension(company.website)} </>
              ) : (
                <EmptyField />
              )}
            </Td>

            <Td sx={{ textAlign: 'right' }}>
              {formatInteger(company.num_active_projects)}
            </Td>
            <Td sx={{ textAlign: 'right' }}>{formatInteger(completedProjectsCount)}</Td>
          </Tr>
        );
      })}
    </Tbody>
  );
};

export default TableBody;
